// src/lib/portfolioComment.js
// 포트폴리오 전략 코멘트 — 레짐 국면 + 포트 스탠스(넷/베타/헤지) + 섹터 쏠림을 엮어
// PM 노트처럼 서술 + 우선순위 제안 + 회귀장에 부합하는 1등급 스프레드 후보.
// 모멘텀 팩터(STM/MTM) 절대 높낮이는 판단하지 않음(주관 영역).
import { SPREAD_SCREEN, SCREEN_ASOF } from "./spreadScreen";

const TH = {
  corrWeak: 0.3, corrStrong: 0.6,
  sideConc: 0.35,   // 한 사이드 top섹터 비중
  netTilt: 12,      // 섹터 순노출 |%p|
  netExp: 12, betaHi: 0.2, betaLo: 0.1,
  crowdStm: 1.0,    // 롱 가중 STM(Z) — 1SD↑면 최근 강세 종목 쏠림(크라우디드)
  crowdStrong: 1.5, // 강한 크라우딩
  factorNet: 1.2,   // 롱-숏 가중 STM 차 — 모멘텀 팩터 순노출
  inflExtreme: 0.1, // 레짐 게이지 백분위 극단(≤10% or ≥90%) → 변곡 구간
  flowHi: 0.03,     // 롱 가중 수급강도(최근5일 외인+기관 순매수/거래량) — 누적매수
};
const pct1 = (n) => (n == null || isNaN(n)) ? "—" : `${(n * 100).toFixed(1)}%`;
const spct = (n) => (n == null || isNaN(n)) ? "—" : `${n >= 0 ? "+" : ""}${(n * 100).toFixed(1)}%`;

// 빈 섹터를 ETF 신호로 랭킹. 회귀장: 조정(모멘텀↓) + 기관 순매수(수급↑) = 반등 초입 우대.
function rankEmptySectors(empty, signals, isMeanRev) {
  const out = [];
  for (const s of empty) {
    const sig = signals && signals[s];
    if (!sig || (sig.mom == null && sig.intensity5 == null)) continue;
    const mom = sig.mom, flow = sig.intensity5;
    let score = 0;
    if (mom != null) score += (isMeanRev ? -mom : mom) * 5;
    if (flow != null) score += flow * 3;
    out.push({
      sector: s, mom, flow, score,
      txt: `${s}(${mom != null ? `모멘텀 ${spct(mom)}` : ""}${flow != null ? `${mom != null ? "·" : ""}수급 ${spct(flow)}` : ""})`,
    });
  }
  return out.sort((a, b) => b.score - a.score);
}
const f1 = (n) => (n == null || isNaN(n)) ? "—" : Number(n).toFixed(1);
const f2 = (n) => (n == null || isNaN(n)) ? "—" : Number(n).toFixed(2);
const sgn2 = (n) => (n == null || isNaN(n)) ? "—" : `${n >= 0 ? "+" : ""}${Number(n).toFixed(2)}`;

// WICS 대분류 10섹터 (빈 섹터 산출용)
const WICS = ["IT", "금융", "산업재", "경기관련소비재", "건강관리", "소재", "필수소비재", "커뮤니케이션서비스", "에너지", "유틸리티"];

// 섹터 → 대표 KR ETF 코드 (모멘텀·수급 프록시). KR ETF가 섹터와 1:1은 아니라 근사치.
export const SECTOR_ETF = {
  "반도체": "091160",        // KODEX 반도체
  "2차전지": "305720",       // KODEX 2차전지산업
  "IT": "157490",            // KODEX 소프트웨어
  "금융": "091170",          // KODEX 은행
  "산업재": "466920",        // SOL 조선TOP3플러스
  "경기관련소비재": "091180", // KODEX 자동차
  "건강관리": "364970",      // TIGER 바이오TOP10
  "소재": "117460",          // KODEX 에너지화학
  "필수소비재": "227560",    // TIGER 200 생활소비재
  "커뮤니케이션서비스": "300950", // KODEX 게임산업
  "에너지": "457990",        // PLUS 태양광&ESS
  "유틸리티": "487240",      // KODEX AI전력핵심설비
  "로봇": "445290",          // KODEX K-로봇액티브
  "우주": "449450",          // PLUS K방산 (우주·방산 근사)
};
export const SECTOR_ETF_CODES = Object.values(SECTOR_ETF);

// 관심 섹터 전체(15) — watchlist 표시 순서. 로테이션 후보로 승격되는 모집단.
export const MASTER_SECTORS = ["반도체", "2차전지", "IT", "금융", "산업재", "경기관련소비재",
  "건강관리", "소재", "필수소비재", "커뮤니케이션서비스", "에너지", "유틸리티", "원전", "로봇", "우주"];

// 롱 북 섹터별 집계: {sector, share(롱 대비), avgStm, avgFlow}. + 빈 섹터.
function longSectorStats(positions) {
  const agg = {};
  let longW = 0;
  for (const p of positions) {
    if (p.short) continue;
    const s = p.sector || "기타";
    const e = agg[s] || (agg[s] = { w: 0, stmW: 0, sw: 0, flowW: 0, fw: 0 });
    e.w += p.w; longW += p.w;
    if (p.stm != null && !isNaN(p.stm)) { e.stmW += p.w * p.stm; e.sw += p.w; }
    if (p.flow != null && !isNaN(p.flow)) { e.flowW += p.w * p.flow; e.fw += p.w; }
  }
  const rows = Object.entries(agg).map(([sector, e]) => ({
    // share=롱 북 대비, grossShare=그로스 대비(화면 섹터 패널과 같은 분모).
    // 둘을 같이 들고 있어야 "롱의 38.1%"와 화면의 19.2%가 충돌해 보이지 않는다.
    sector, share: longW ? e.w / longW : 0, grossShare: e.w,
    avgStm: e.sw ? e.stmW / e.sw : null,
    avgFlow: e.fw ? e.flowW / e.fw : null,
  })).sort((a, b) => b.share - a.share);
  const held = new Set(rows.filter((r) => r.share > 0.03).map((r) => r.sector));
  const empty = WICS.filter((s) => !held.has(s));
  return { rows, empty, longW };
}

// 사이드(롱/숏) 내 가중평균 팩터값. positions: [{short, w(양의 그로스비중), stm, mtm}]
function sideWeighted(positions, short, field) {
  const ps = positions.filter((p) => p.short === short && p[field] != null && !isNaN(p[field]));
  const tot = ps.reduce((s, p) => s + (p.w || 0), 0);
  if (!tot) return null;
  return ps.reduce((s, p) => s + (p.w / tot) * p[field], 0);
}

// 스크리너 1등급: 안정성 s<-3.43 & |z|>=2. 방향: z<=-2 롱a/숏b, z>=2 숏a/롱b.
// 페어 추가 형식(longTicker/shortTicker...)으로 반환 → 클릭 시 차트북에 그대로 추가 가능.
function grade1Ideas() {
  return SPREAD_SCREEN
    .filter((d) => d.s < -3.43 && Math.abs(d.z) >= 2)
    .sort((a, b) => Math.abs(b.z) - Math.abs(a.z))
    .slice(0, 3)
    .map((d) => {
      const [L, S] = d.z <= -2
        ? [{ t: d.ca, n: d.a, m: d.ma }, { t: d.cb, n: d.b, m: d.mb }]
        : [{ t: d.cb, n: d.b, m: d.mb }, { t: d.ca, n: d.a, m: d.ma }];
      return {
        longTicker: L.t, longName: L.n, longMarket: L.m,
        shortTicker: S.t, shortName: S.n, shortMarket: S.m,
        sector: d.t, z: d.z,
      };
    });
}

export function buildComments({ positions = [], sectorRows = [], momo = null, corr = null, regime = null, sectorSignals = null, liveNetPct = null }) {
  const points = [];
  const push = (level, text) => points.push({ level, text });

  const rgm = regime?.regime ? String(regime.regime) : null;
  const isMeanRev = rgm && rgm.includes("회귀");
  const isMomentum = rgm && rgm.includes("모멘텀");
  const strength = regime?.strength ? `(${regime.strength})` : "";
  const crash = regime?.crash_zone ? "·크래시존" : "";
  const pctile = regime?.pctile != null ? Number(regime.pctile) : null;

  // 롱/숏 북의 가중 모멘텀(크라우딩 신호)
  const lStm = sideWeighted(positions, false, "stm");
  const lMtm = sideWeighted(positions, false, "mtm");
  const sStm = sideWeighted(positions, true, "stm");
  const lFlow = sideWeighted(positions, false, "flow");   // 롱 가중 수급강도(외인+기관)
  const crowdedLong = lStm != null && lStm >= TH.crowdStm;

  // 섹터 파생
  const longTot = sectorRows.reduce((s, r) => s + r.longPct, 0);
  const shortTot = sectorRows.reduce((s, r) => s + r.shortPct, 0);
  const topLong = [...sectorRows].sort((a, b) => b.longPct - a.longPct)[0];
  const topShort = [...sectorRows].sort((a, b) => b.shortPct - a.shortPct)[0];
  const tilts = sectorRows.map((r) => ({ sector: r.sector, tilt: r.longPct - r.shortPct }))
    .sort((a, b) => Math.abs(b.tilt) - Math.abs(a.tilt));
  const maxTilt = tilts[0];

  // 넷 노출은 실시간 보유 기준을 우선한다. momo.net은 어제자 엑셀 스냅샷이라
  // 오늘 포지션을 조정하면 코멘트만 옛 수치를 말하게 된다.
  const netPct = liveNetPct != null ? liveNetPct
    : (momo?.net != null ? momo.net * 100 : null);
  const beta = momo?.beta;
  const neutral = beta != null && Math.abs(beta) <= TH.betaLo && (netPct == null || Math.abs(netPct) <= TH.netExp);

  // ── 1) 헤드라인: 국면 + 스탠스 한 줄 ──
  const stance = neutral ? "시장중립을 유지"
    : (beta != null && Math.abs(beta) > TH.betaHi) ? `넷 베타 ${f2(beta)}로 방향성 노출`
    : (netPct != null && Math.abs(netPct) > TH.netExp) ? `넷 ${netPct > 0 ? "롱" : "숏"} ${f1(Math.abs(netPct))}%로 방향성 존재`
    : "대체로 중립";
  const crowdTag = (isMeanRev && crowdedLong) ? " 다만 롱이 최근 승자에 쏠려 리버전 위험이 있습니다." : "";
  const headline = (rgm
    ? `시장은 ${rgm}${strength} 국면 — 포트폴리오는 ${stance} 중입니다.`
    : `포트폴리오는 ${stance} 중입니다.`) + crowdTag;

  // ── 2) 국면 해석 ──
  if (regime?.playbook)
    push("info", `국면 진단: ${regime.playbook}.`);
  else if (isMeanRev)
    push("info", "평균회귀 국면 — 극단 이격의 되돌림이 우위이고, 모멘텀·팩터 쏠림은 역풍입니다.");
  else if (isMomentum)
    push("info", "모멘텀 국면 — 추세·승자 유지가 우위입니다.");

  // ── 3) 포트 스탠스(넷/베타/헤지) ──
  if (beta != null) {
    if (neutral)
      push("good", `넷 베타 ${f2(beta)}·넷 노출 ${netPct == null ? "—" : (netPct > 0 ? "+" : "") + f1(netPct) + "%"} — 시장 방향성은 잘 중화돼 있습니다${isMeanRev ? ". 회귀장에서 중립 스탠스는 적절합니다" : ""}.`);
    else if (Math.abs(beta) > TH.betaHi)
      push(isMeanRev ? "warn" : "info", `넷 베타 ${f2(beta)} — 시장 방향성 노출이 남아 있습니다${isMeanRev ? `. 회귀장${strength}에선 방향성 베팅이 역풍일 수 있어 베타 축소를 검토하세요` : ""}.`);
  }
  if (corr != null && !isNaN(corr)) {
    if (corr < TH.corrWeak)
      push("warn", `롱·숏 상관 ${f2(corr)}로 낮습니다 — 숏이 롱을 헤지하지 못해 시장 급락에 취약합니다. 롱·숏의 섹터·베타 구성을 근접시켜 헤지를 보강하세요.`);
    else if (corr >= TH.corrStrong)
      push("good", `롱·숏 상관 ${f2(corr)} — 숏이 롱을 잘 받쳐 시장중립적으로 헤지돼 있습니다.`);
    else
      push("info", `롱·숏 상관 ${f2(corr)} — 부분 헤지 상태로, 순수 종목선택 알파와 시장 노출이 섞여 있습니다.`);
  }

  // ── 3.5) 크라우딩 / 리버전 (롱이 최근 강세 종목에 쏠렸나 × 국면 × 수급) ──
  const flowBought = lFlow != null && lFlow >= TH.flowHi;   // 외인+기관이 롱을 순매수 누적
  if (lStm != null) {
    if (isMeanRev && crowdedLong) {
      const strong = lStm >= TH.crowdStrong || regime?.crash_zone || flowBought;
      const flowClause = flowBought
        ? ` 게다가 최근 5일 외인·기관이 롱 종목을 순매수(수급강도 +${pct1(lFlow)})로 담아 크라우딩이 실제 수급으로 확인됩니다 — 되돌림 시 이 물량이 매도압력이 됩니다.`
        : "";
      push(strong ? "warn" : "info",
        `롱 북이 최근 강세 종목에 쏠려 있습니다(가중 STM ${sgn2(lStm)}). ${rgm}${crash} 국면에선 이렇게 "잘 버틴 승자"들의 되돌림(리버전) 확률이 높습니다.${flowClause} → STM 최상위 종목의 비중을 줄이고 저모멘텀·비상관 섹터로 분산하세요.`);
    } else if (isMomentum && crowdedLong) {
      push("good", `롱이 모멘텀 국면에 부합하는 강세 종목 중심입니다(가중 STM ${sgn2(lStm)}) — 유지하되 트레일링 손절 규칙을 지키세요.`);
    }
    if (lMtm != null && isMeanRev && lMtm >= TH.crowdStm && !crowdedLong)
      push("info", `롱의 중기(6M) 모멘텀이 높습니다(가중 MTM ${sgn2(lMtm)}) — 회귀장에선 중기 승자도 되돌림 대상이라 일부 이익실현을 고려하세요.`);
  }
  // 장세 정합성 — 포트가 국면을 따라가나 / 방어적 레거드인가
  if (isMomentum && lStm != null) {
    if (lStm < 0.5 && (beta == null || Math.abs(beta) <= TH.betaHi))
      push("warn", `모멘텀(상승) 국면인데 포트의 모멘텀 노출이 약합니다(롱 가중 STM ${sgn2(lStm)}${beta != null ? `·넷 베타 ${f2(beta)}` : ""}) — 장세를 못 따라가는 방어적/레거드 포지션입니다. 베타·모멘텀 노출을 늘려 장세에 편승하는 것을 검토하세요.`);
    else if (lStm >= TH.crowdStm)
      push("good", `모멘텀 국면에 부합 — 강세 종목 보유로 장세를 잘 따라가는 중입니다(롱 가중 STM ${sgn2(lStm)}).`);
    else
      push("info", `모멘텀 국면 — 포트 모멘텀 노출이 중간(롱 가중 STM ${sgn2(lStm)})이라 장세를 부분적으로만 따라갑니다.`);
  } else if (isMeanRev && lStm != null && lStm < TH.crowdStm && !crowdedLong) {
    push("good", `회귀장에는 저모멘텀·중립 스탠스가 적절 — 방어적이나 국면에 부합합니다.`);
  }
  // 모멘텀 팩터 순노출(롱-숏)
  if (lStm != null && sStm != null && Math.abs(lStm - sStm) >= TH.factorNet) {
    const net = lStm - sStm;
    push("info", `포트가 모멘텀 팩터에 ${net > 0 ? "순롱" : "순숏"} 노출입니다(가중 STM 롱 ${sgn2(lStm)} vs 숏 ${sgn2(sStm)}) — ${net > 0 ? "모멘텀 크래시(급반전) 시 취약" : "모멘텀 지속 시 역풍"}.`);
  }
  // 수급(외인+기관) 단독 신호 — 항상 롱 북 수급 방향 표면화
  if (lFlow != null) {
    if (lFlow <= -TH.flowHi)
      push("warn", `롱 종목에서 외인·기관이 최근 5일 순매도 중입니다(수급강도 ${spct(lFlow)}) — 수급 이탈 신호. 실적·뉴스 등 이유를 점검하고 비중을 재검토하세요.`);
    else if (flowBought && !isMeanRev)
      push("info", `외인·기관이 롱 종목을 순매수(수급강도 ${spct(lFlow)}) — 수급은 우호적이나, 이미 담긴 만큼 신규 추격은 신중히.`);
    else if (!flowBought)
      push("info", `롱 종목 최근 5일 외인·기관 수급강도 ${spct(lFlow)} — 뚜렷한 쏠림 없음(중립).`);
  }
  // 변곡점(레짐 게이지 극단 / 크래시존)
  if (pctile != null && (pctile <= TH.inflExtreme || pctile >= 1 - TH.inflExtreme))
    push("info", `레짐 게이지가 극단 구간(역사 백분위 ${Math.round(pctile * 100)}%)${crash}입니다 — 국면 전환이 임박할 수 있어 신규 방향성 베팅은 확인 후 진입하세요.`);

  // ── 4) 섹터 포지셔닝 → 로테이션 (항상 표시: 크라우디드 정리 / 빈 섹터로 이동) ──
  const secStat = longSectorStats(positions);
  const topSec = secStat.rows[0];
  if (topSec && secStat.longW > 0) {
    const ranked = rankEmptySectors(secStat.empty, sectorSignals, isMeanRev);
    const targetTxt = ranked.length
      ? ranked.slice(0, 2).map((r) => r.txt).join(", ")
      : (secStat.empty.slice(0, 3).join("·") || "");
    const crowdBits = [`롱 북의 ${f1(topSec.share * 100)}%(그로스 대비 ${f1(topSec.grossShare * 100)}%)`];
    if (topSec.avgStm != null && topSec.avgStm >= TH.crowdStm) crowdBits.push(`가중 STM ${sgn2(topSec.avgStm)}`);
    if (topSec.avgFlow != null && topSec.avgFlow >= TH.flowHi) crowdBits.push(`수급 +${pct1(topSec.avgFlow)}`);
    const crowded = topSec.share > TH.sideConc && (crowdBits.length > 1 || isMeanRev);

    if (crowded) {
      push(topSec.share > 0.5 || crowdBits.length > 1 ? "warn" : "info",
        `롱이 '${topSec.sector}'에 크라우디드입니다(${crowdBits.join("·")})${isMeanRev ? " — 회귀장에선 되돌림 위험↑" : ""}. 일부 정리하고 ${targetTxt ? `들어갈 만한 빈 섹터: ${targetTxt}` : "저크라우딩 섹터"}로 로테이션하세요.`);
    } else {
      const top3 = secStat.rows.slice(0, 3).map((r) => `${r.sector} ${f1(r.share * 100)}%`).join(" · ");
      push("info", `롱 섹터 배분: ${top3}.${targetTxt ? ` 노출 없고 지금 들어갈 만한 섹터: ${targetTxt}.` : (secStat.empty.length ? ` 노출 없는 섹터: ${secStat.empty.slice(0, 3).join("·")}.` : "")}`);
    }
  }
  if (topShort && shortTot > 0 && topShort.shortPct / shortTot > TH.sideConc)
    push("info", `숏은 '${topShort.sector}'에 ${f1((topShort.shortPct / shortTot) * 100)}% 집중돼 있습니다.`);
  if (maxTilt && Math.abs(maxTilt.tilt) > TH.netTilt)
    push("info", `'${maxTilt.sector}' 순노출 ${maxTilt.tilt > 0 ? "+" : ""}${f1(maxTilt.tilt)}%p — 사실상 ${maxTilt.tilt > 0 ? "롱" : "숏"} 방향의 섹터 베팅입니다. 의도된 것인지 점검하세요.`);

  // ── 5) 제안 합성(우선순위) ──
  const actions = [];
  if (isMeanRev && crowdedLong) actions.push("롱 최상위 STM(승자) 비중 축소·저모멘텀 분산");
  if (corr != null && corr < TH.corrWeak) actions.push("숏 헤지 보강(롱·숏 구성 근접)");
  if (beta != null && Math.abs(beta) > TH.betaHi && isMeanRev) actions.push("넷 베타 축소");
  if (topSec && topSec.share > TH.sideConc)
    actions.push(`'${topSec.sector}' 정리 → ${secStat.empty.slice(0, 2).join("·") || "빈 섹터"}로 로테이션`);
  if (maxTilt && Math.abs(maxTilt.tilt) > TH.netTilt) actions.push(`'${maxTilt.sector}' 순노출 조정`);
  if (actions.length)
    push("warn", `→ 우선순위: ${actions.map((a, i) => `${i + 1}) ${a}`).join(" · ")}.`);
  else if (points.every((p) => p.level !== "warn"))
    push("good", "→ 균형 양호 — 현 스탠스 유지, 개별 종목 이격에서 알파를 노리는 국면입니다.");

  // ── 6) 회귀장 부합 아이디어(1등급 스프레드) ──
  const ideas = (isMeanRev || !rgm) ? grade1Ideas() : [];

  // 로테이션 후보(빈 섹터) — 신호 있는 것 우선 랭킹, 나머지도 포함(클릭용)
  const rankedEmpty = rankEmptySectors(secStat.empty, sectorSignals, isMeanRev);
  const rankedSet = new Set(rankedEmpty.map((r) => r.sector));
  const rotationTargets = [...rankedEmpty, ...secStat.empty.filter((s) => !rankedSet.has(s)).map((s) => ({ sector: s }))];

  return { headline, points, ideas, ideasAsof: SCREEN_ASOF, ideasRegime: isMeanRev, rotationTargets };
}
